import { defineComponent as _defineComponent } from 'vue'
import {
  Transaction,
  isTrade,
  isDeposit,
  isWithdraw,
  HistoricalTransaction,
  TransactionExternal,
  TransactionState,
  Trade,
  Beneficiary,
  ExportListType,
  beneficiaryName,
  TransactionInternal,
  isSchedule,
  getBuyCcy,
  getSellCcy,
  ScheduledTransaction,
  AmountType,
  getPrimaryCcy,
  BankingScheme,
  getCurrencyPair,
  transactionStateLabels,
  AuthorityType,
  hedgingInstrumentToHuman,
  isPartnerPayment,
} from 'ah-api-gateways';
import WithdrawFromWalletModal from 'ah-wallets/src/components/WithdrawFromWalletModal.vue';
import TransactionReasonComponent from 'ah-wallets/src/components/transactions/TransactionReason.vue';
import { maskedIban } from 'ah-common-lib/src/helpers/iban';
import { formatDate } from 'ah-common-lib/src/helpers/time';
import { useRequestManager } from 'ah-common-lib/src/requestManager/useRequestManager';
import { computed, reactive, ref, watch } from 'vue';
import { useAhWalletsState } from '../..';
import { VButton } from 'ah-common-lib/src/common/components';
import { useOnBehalfOf } from 'ah-common-lib/src/onBehalfOf/useInjectedOBO';
import WalletsFormattedCurrency from '../../currency/WalletsFormattedCurrency.vue';


export default /*#__PURE__*/_defineComponent({
  __name: 'TransactionDetails',
  props: {
    transaction: null,
    hideState: { type: [Boolean, String], default: true }
  },
  emits: ["withdraw-succeded"],
  setup(__props: any, { emit }: { emit: ({
  /*
   * Emits:
   * - transaction-cancel (transaction: Transaction): triggered when cancel payment is clicked
   */
  (e: 'withdraw-succeded', value: void): void;
}), expose: any, slots: any, attrs: any }) {

const props = __props as { transaction: Transaction, hideState: boolean | string };



const requestManager = useRequestManager({
  exposeToParent: ['getTransactionBeneficiary', 'getTransactionTrade'],
  onRetryFromParentManager: (k: string) => {
    if (k === 'getTransactionBeneficiary') {
      loadBeneficiaryDetails();
    } else if (k === 'getTransactionTrade') {
      loadTradeDetails();
    }
  },
});

const state = reactive<{
  transactionTrade: Trade | null;
  beneficiary: Beneficiary | null;
  showTransactionDetailsDownloadPopover: boolean;
}>({
  transactionTrade: null,
  beneficiary: null,
  showTransactionDetailsDownloadPopover: false,
});

const walletState = useAhWalletsState();

const downloadButton = ref<InstanceType<typeof VButton> | null>(null);

const onBehalfOfClient = useOnBehalfOf();



walletState.store.useWalletsStore().loadCurrencyWallet({
  currency: (props.transaction as TransactionInternal).currency,
});

walletState.store.useSettingsStore().loadRoutingCodeTypes();

const isFailedTx = computed(() => (props.transaction as HistoricalTransaction).state === TransactionState.FAILED);

const isRejectedTx = computed(() => (props.transaction as HistoricalTransaction).state === TransactionState.REJECTED);

const wallet = computed(() => {
  let wallet = walletState.store
    .useWalletsStore()
    .getWalletById((props.transaction as TransactionInternal).otherWalletId);
  if (!wallet) {
    wallet = walletState.store.useWalletsStore().getWallet(
      (props.transaction as TransactionInternal).currency,
      onBehalfOfClient.value
        ? {
            isPartner: !(onBehalfOfClient.value?.id || walletState.store.useAuthStore().isClientUser),
            id: onBehalfOfClient.value?.id,
          }
        : undefined
    );
  }
  return wallet ?? null;
});

const transactionRoutingCode = computed(() => {
  const routingData = walletState.store
    .useSettingsStore()
    .routingCodes.find((r) => r.routingCodeType === (props.transaction as TransactionExternal).otherRoutingCode);
  if (routingData) {
    return routingData.title;
  }
  return 'Routing Code';
});

const transactionBeneficiaryName = computed(() => (state.beneficiary ? beneficiaryName(state.beneficiary) : ''));

const loadingTrade = computed(() => requestManager.manager.requestStates['getTransactionTrade'] === 'pending');

const loadingBeneficiary = computed(
  () => requestManager.manager.requestStates['getTransactionBeneficiary'] === 'pending'
);

const canDownloadStatement = computed(() =>
  walletState.store.useAuthStore().hasAuthorities(AuthorityType.EXPORT_TRANSACTIONS)
);

/**
 * Partners/Traders can cancel transactions via OBO, as can Client users with the correct authority
 */
const canCancelTransaction = computed(() => {
  if (onBehalfOfClient.value) {
    return true;
  }
  if (props.transaction.tradeId) {
    return walletState.store.useAuthStore().hasAuthorities(AuthorityType.UPDATE_TRADES);
  }
  return true;
});

const isScheduledTransaction = computed(() => isSchedule(props.transaction));

const isPartnerMadePayment = computed(() => isPartnerPayment(props.transaction));

const detailsAvailable = computed(() => {
  return (
    !loadingBeneficiary.value &&
    !loadingTrade.value &&
    (isTransactionDeposit.value ||
      isTransactionWithdraw.value ||
      (isTransactionTrade.value && state.transactionTrade) ||
      isPartnerMadePayment.value)
  );
});

function getMarkupValue(totalMarkupBasisPoints: number) {
  const out = Math.round(totalMarkupBasisPoints);
  if (!isNaN(out)) {
    return out + 'bp';
  } else {
    return '-';
  }
}

function toggleTransactionDetailsDownloadPopover(value?: boolean) {
  setTimeout(() => {
    state.showTransactionDetailsDownloadPopover = value ?? !state.showTransactionDetailsDownloadPopover;
  });
}

const buyData = computed(() => {
  if (state.transactionTrade) {
    return getBuyCcy(state.transactionTrade);
  }
  return '';
});

const sellData = computed(() => {
  if (state.transactionTrade) {
    return getSellCcy(state.transactionTrade);
  }
  return '';
});

const currencyPair = computed(() => {
  if (state.transactionTrade) {
    return getCurrencyPair(state.transactionTrade);
  }
  return '';
});

const fxRate = computed(() => {
  if (state.transactionTrade) {
    return getPrimaryCcy(state.transactionTrade).clientRate;
  }
  return '';
});

const isTransactionTrade = computed(() => isTrade(props.transaction));

const isTransactionDeposit = computed(() => isDeposit(props.transaction));

const isTransactionWithdraw = computed(() => isWithdraw(props.transaction));

const isSwiftWithdraw = computed(
  () => isTransactionWithdraw.value && state.beneficiary?.bankingScheme === BankingScheme.SWIFT
);

const transactionConfirmed = computed(
  () => (props.transaction as HistoricalTransaction).state === TransactionState.CONFIRMED
);

const isOpenPayment = computed(() => !!(props.transaction as ScheduledTransaction).executionDate);

const isRelatedToQuote = computed(() => props.transaction.quoteId);

const targetData = computed(() => {
  if (state.transactionTrade) {
    const primaryCurr = getPrimaryCcy(state.transactionTrade);
    if (primaryCurr.amountType === AmountType.SELL) {
      return sellData.value ?? {};
    }
    return buyData.value ?? {};
  }
  return {};
});

const stateLabel = computed(() => {
  if ((props.transaction as HistoricalTransaction).state) {
    return transactionStateLabels[(props.transaction as HistoricalTransaction).state];
  }
  return '';
});

function loadTradeDetails() {
  if (props.transaction.tradeId) {
    requestManager.manager
      .sameOrCancelAndNew(
        'getTransactionTrade',
        walletState.services.trade.getTrade(props.transaction.tradeId),
        props.transaction.tradeId
      )
      .subscribe((trade) => {
        state.transactionTrade = trade;
      });
  }
}

watch(() => props.transaction, loadTradeDetails, { immediate: true });

function downloadPaymentMt103(type: ExportListType) {
  if (props.transaction.paymentId) {
    requestManager.manager
      .sameOrCancelAndNew(
        'downloadPaymentMt103',
        walletState.services.payments.downloadPaymentMt103(
          props.transaction.paymentId,
          type,
          `MT103 Statement${onBehalfOfClient.value?.name ? ` for client ${onBehalfOfClient.value.name}` : ''}`
        ),
        props.transaction.tradeId
      )
      .subscribe();
  }
}

function redirectToBeneficiary(id: string) {
  return onBehalfOfClient.value
    ? `/admin/activity/clients/${onBehalfOfClient.value.id}/beneficiaries/${id}`
    : `/dashboard/beneficiaries/${id}`;
}

function downloadTransactionDetails(type: ExportListType) {
  state.showTransactionDetailsDownloadPopover = false;
  if (props.transaction && !isScheduledTransaction.value) {
    requestManager.manager
      .sameOrCancelAndNew(
        'downloadTransactionTrade',
        walletState.services.wallet.downloadTransactionDetails(
          (props.transaction as HistoricalTransaction).walletId,
          (props.transaction as HistoricalTransaction).id,
          type
        ),
        (props.transaction as HistoricalTransaction).id
      )
      .subscribe();
  }
}

function loadBeneficiaryDetails() {
  const id = state.transactionTrade?.beneficiaryId || (props.transaction as TransactionExternal).otherBeneficiaryId;
  if (id) {
    requestManager.manager
      .sameOrCancelAndNew('getTransactionBeneficiary', walletState.services.beneficiary.getBeneficiary(id), id)
      .subscribe((beneficiary) => {
        state.beneficiary = beneficiary;
      });
  }
}

watch(() => state.transactionTrade, loadBeneficiaryDetails, { immediate: true });

function onWithdrawSucceeded() {
  emit('withdraw-succeded');
}

const isClient = computed(() => walletState.store.useAuthStore().isClientUser);

function routerLink(referenceNumber: string) {
  return isClient.value
    ? `/dashboard/trades/all/` + referenceNumber
    : { path: `/admin/activity/reports`, query: { trade: referenceNumber } };
}

return { __sfc: true,props, requestManager, state, walletState, downloadButton, onBehalfOfClient, emit, isFailedTx, isRejectedTx, wallet, transactionRoutingCode, transactionBeneficiaryName, loadingTrade, loadingBeneficiary, canDownloadStatement, canCancelTransaction, isScheduledTransaction, isPartnerMadePayment, detailsAvailable, getMarkupValue, toggleTransactionDetailsDownloadPopover, buyData, sellData, currencyPair, fxRate, isTransactionTrade, isTransactionDeposit, isTransactionWithdraw, isSwiftWithdraw, transactionConfirmed, isOpenPayment, isRelatedToQuote, targetData, stateLabel, loadTradeDetails, downloadPaymentMt103, redirectToBeneficiary, downloadTransactionDetails, loadBeneficiaryDetails, onWithdrawSucceeded, isClient, routerLink, ExportListType, hedgingInstrumentToHuman, WithdrawFromWalletModal, TransactionReasonComponent, maskedIban, formatDate, VButton, WalletsFormattedCurrency }
}

})